import { Fragment, useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { useHistory, useLocation } from 'react-router';
import { useMutation } from 'react-query';
import { getHome, updateNewAnswer } from '@/api';
import ReceiptLayout from '@/components/Receipt/ReceiptLayout';
import TopSection from '@/components/Home/TopSection';
import BottomSection from '@/components/Home/BottomSection';
import { IGlobalState } from '@/store';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks';
import {
  IVerifyPassportPayload,
  IVerifyPassportResponse,
} from '@/types/common.type';
import LoadingView from '@/components/common/LoadingView';
import { getDday } from '@repo/utils';
import { IData } from '@/components/Receipt/RefundReceipt';
import { AnswerdModal } from '@repo/components';
import { routes } from '@/constants/routes.const';
import { MTCN } from '@repo/types';

type Params = {
  scrollToReceipt?: boolean;
};
function Home() {
  const dispatch = useAppDispatch();
  const params = useLocation().state as Params;
  const scrollToReceipt = params?.scrollToReceipt;
  const history = useHistory();
  const {
    isUploadRequired,
    passportNumber,
    refundAfter,
    hasNewAnswer,
    printData,
    nationality,
  } = useAppSelector((state) => state) as IGlobalState;
  const [mtcnList, setMtcnList] = useState<MTCN[]>([]);
  const LANG = nationality === 'KOR' ? 'KOR' : 'ENG';

  const onCloseModal = () => {
    dispatch({
      type: 'UPDATE_HOME_MODAL_STATE',
      payload: { isActive: false, data: null, type: null },
    });
  };

  const { mutate: homeMutation, isLoading } = useMutation<
    IVerifyPassportResponse,
    AxiosError,
    IVerifyPassportPayload
  >((payload) => getHome(payload), {
    retry: false,
    onSuccess: ({
      register,
      read,
      receiptUpload,
      departureStatus,
      paymentStatus,
      approvedCount,
      unapprovedCount,
      refundAmount,
      refundAfterAmount,
      email,
      refundCondition,
      maximumTransferDate,
      departureDate,
      hasNewAnswer,
      customerNation,
      refundedIn,
      refundedDate,
      refundAmountDetail,
      ...rest
    }) => {
      dispatch({
        type: 'UPDATE_USER_INFO',
        payload: {
          isRefundInfoRegistered: register,
          haveSeenReceipt: read,
          isReceiptImageUploaded: receiptUpload,
          isCustomerLeft: isUploadRequired
            ? getDday(departureDate as string) === 'D-DAY'
            : departureStatus === 'DEPARTURE_COMPLETE',
          paymentStatus,
          approvedCount,
          unapprovedCount,
          refundAmount,
          refundAfterAmount,
          email,
          refundCondition,
          maximumTransferDate,
          departureDate,
          hasNewAnswer,
          nationality: customerNation,
          refundedIn,
          refundedDate,
          refundAmountDetail,
          ...rest,
        },
      });
      setMtcnList(rest.mtcnInfo?.mtcnList || []);
    },
  });
  const fetchHome = () => {
    homeMutation({ passportNumber: passportNumber as string });
  };

  useEffect(() => {
    fetchHome();
  }, [passportNumber]);

  useEffect(() => {
    const preventGoBack = () => {
      onCloseModal();
      history.go(1);
    };
    window.addEventListener('popstate', preventGoBack);
    return () => window.removeEventListener('popstate', preventGoBack);
  }, []);

  const previewReceipt = () => {
    dispatch({
      type: 'UPDATE_HOME_MODAL_STATE',
      payload: {
        type: 'PREVIEW_RECEIPT',
        isActive: true,
        data: { data: (printData as IData[])[0] },
      },
    });
  };

  useEffect(() => {
    if (localStorage.getItem('isCompleteRefund')) {
      previewReceipt();
      localStorage.removeItem('isCompleteRefund');
    }
  }, []);

  const { mutate: readAnswer } = useMutation(
    () => updateNewAnswer(passportNumber || ''),
    {
      onSuccess: () => {
        sessionStorage.setItem('inquiry-endpoint', routes.home);
        history.push(routes.inquiryQna);
      },
      onError: (error: any) => {
        alert(error.message);
      },
    },
  );

  return (
    <ReceiptLayout hasPadding={false} style={{ paddingBottom: 56 }}>
      {isLoading ? (
        <LoadingView />
      ) : (
        <Fragment>
          <TopSection mtcnList={mtcnList} />
          {refundAfter && (
            <BottomSection
              refetchHome={fetchHome}
              scrollToReceipt={scrollToReceipt}
            />
          )}
        </Fragment>
      )}
      <AnswerdModal isOpen={hasNewAnswer} onClick={readAnswer} lang={LANG} />
    </ReceiptLayout>
  );
}
export default Home;
